/**
 * Integer-cent money helpers. ADR-0002.
 *
 * `185.10 * 3` in IEEE-754 is not `555.30`, and this product's entire
 * credibility rests on its dollar figures being right. Nothing in the
 * monetary path may be a float.
 */
/**
 * Parse a decimal money string (e.g. `"185"`, `"185.10"`, `"$1,185.10"`) into
 * integer cents, without ever multiplying a float by 100.
 *
 * Returns `null` for anything unparseable — callers decide what that means.
 */
export function parseMoneyToCents(input) {
    const raw = typeof input === 'number' ? String(input) : input;
    const cleaned = raw.trim().replace(/[$\s,]/g, '');
    if (cleaned === '')
        return null;
    const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(cleaned);
    if (!match)
        return null;
    const [, sign, whole = '', frac = ''] = match;
    if (whole === '' && frac === '')
        return null;
    // Take two decimal places, rounding the third half-up, all in integers.
    const centsDigits = (frac + '00').slice(0, 2);
    const thirdDigit = frac.length > 2 ? Number(frac[2]) : 0;
    let cents = Number(whole || '0') * 100 + Number(centsDigits);
    if (thirdDigit >= 5)
        cents += 1;
    return sign === '-' ? -cents : cents;
}
/**
 * Multiply integer cents by a decimal quantity (hours) and return integer
 * cents. The float appears only as the multiplicand; the result is rounded
 * once, at the end. This is the single sanctioned cents-by-hours operation.
 */
export function centsTimesHours(cents, hours) {
    return Math.round(cents * hours);
}
/**
 * Format integer cents for display. Rounding happens only here — never
 * mid-calculation.
 *
 * Currency is a parameter because no document fixes one: `sample_files/` carry
 * a bare `rate` column with no unit, and BP.md never names a currency. The
 * default renders an unadorned `$`; a real currency decision belongs to the
 * product, not to this helper.
 */
export function formatCents(cents, currency = 'USD', locale = 'en-US') {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(cents / 100);
}
//# sourceMappingURL=money.js.map